import { FormEvent, useState } from "react";

import CodiceFiscale from "codice-fiscale-js";
import { acceptOfferPayee } from "api/acceptOfferPayee";
import { useAtomValue, useSetAtom } from "jotai";
import { sessionIdAtom } from "atoms/sessionId";
import { clientIdAtom } from "atoms/clientId";
import { payeeFinalisedAtom } from "atoms/payeeFinalised";

enum Country {
  IT = "IT",
}

const validateIban = (iban: string) => {
  // Remove spaces and to upper case
  iban = iban.replace(/\s+/g, '').toUpperCase();
    
  // Check the basic format
  const basicFormat = /^[A-Z0-9]+$/;
  if (!basicFormat.test(iban)) {
      return false;
  }
  
  // Check the length of the IBAN based on the country
  const ibanLengths: { [countryCode: string]: number } = {
      AL: 28, AD: 24, AT: 20, AZ: 28, BH: 22, BE: 16, BA: 20, BR: 29,
      BG: 22, CR: 21, HR: 21, CY: 28, CZ: 24, DK: 18, DO: 28, EE: 20,
      FO: 18, FI: 18, FR: 27, GE: 22, DE: 22, GI: 23, GR: 27, GL: 18,
      GT: 28, HU: 28, IS: 26, IE: 22, IL: 23, IT: 27, JO: 30, KZ: 20,
      KW: 30, LV: 21, LB: 28, LI: 21, LT: 20, LU: 20, MT: 31, MR: 27,
      MU: 30, MC: 27, MD: 24, ME: 22, NL: 18, MK: 19, NO: 15, PK: 24,
      PS: 29, PL: 28, PT: 25, QA: 29, RO: 24, SM: 27, SA: 24, RS: 22,
      SK: 24, SI: 19, ES: 24, SE: 24, CH: 21, TN: 24, TR: 26, AE: 23,
      GB: 22, VG: 24, XK: 20
  };
  
  const countryCode = iban.slice(0, 2);
  const expectedLength = ibanLengths[countryCode];
  
  if (expectedLength === undefined || iban.length !== expectedLength) {
      return false;
  }
  
  // Rearrange the IBAN
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  
  // Convert letters to numbers (A=10, B=11, ..., Z=35)
  const converted = rearranged.split('').map(char => {
      const code = char.charCodeAt(0);
      return (code >= 65 && code <= 90) ? (code - 55) : char;
  }).join('');
  
  // Perform mod-97 operation
  const mod97 = (numStr: string): number => {
      let remainder = numStr;
      let block;
      while (remainder.length > 2) {
          block = remainder.slice(0, 9);
          remainder = parseInt(block, 10) % 97 + remainder.slice(block.length);
      }
      return parseInt(remainder, 10) % 97;
  };
  
  return mod97(converted) === 1;
}

const validateSsn = (ssn: string, country: Country): boolean => {
    return {
      "IT": (ssn: string) => CodiceFiscale.check(ssn)
    }[country](ssn)
};

export const usePaymentDetailsForm = () => {
  const initialInput = ""
  const [iban, setIban] = useState(initialInput);
  const [ssn, setSsn] = useState(initialInput);
  const [name, setName] = useState(initialInput);
  const sessionId = useAtomValue(sessionIdAtom);
  const clientId = useAtomValue(clientIdAtom);

  const isValidIban = iban ? validateIban(iban) : false;
  const isValidSsn = ssn ? validateSsn(ssn, Country.IT) : false;
  const isValidName = name ? true : false;
  const setPayeeFinalised = useSetAtom(payeeFinalisedAtom);
  const handleSubmit = (onSubmit: (data: { 
      iban: string | undefined,
      ssn: string | undefined,
      name: string | undefined}) => void) => async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await acceptOfferPayee(sessionId, clientId, iban, ssn, name)
    setPayeeFinalised(true);
    onSubmit({iban: iban!, ssn: ssn!, name: name!});
  };

  const onTypeIban = (value: string) => {
    if (value !== undefined) setIban(value);
  };

  const onTypeSsn = (value: string) => {
    if (value !== undefined) setSsn(value);
  };

  const onTypeName = (value: string) => {
    if (value !== undefined) setName(value);
  };

  return {
    handleSubmit,
    iban,
    ssn,
    name,
    onTypeIban,
    onTypeSsn,
    onTypeName,
    isValidIban,
    isValidSsn,
    isValidName
  };
};
